import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Slider,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";

import {
  BooleanInput,
  Button,
  FormDataConsumer,
  Labeled,
  NumberInput,
  TextField,
  useNotify,
} from "react-admin";
import { useField, useForm } from "react-final-form";

import { Auth } from "aws-amplify";
import { Signer } from "@aws-amplify/core";
import { Geo } from "@aws-amplify/geo";

import { logError } from "../../../utils/appUtils";
import awsExports from "../../../aws-exports";
import { pointInGeofence } from "../../../driverApp/geo";

// maplibre-gl is loaded as a global via CDN in public/index.html (avoids CRA 4 / webpack 4 parse issue)

// Amplify's Location Service config (awsExports.geo) is absent in environments where
// the geo category was never pulled. Read it defensively: a throw at module scope
// happens during import/evaluation — before any component mounts — so a React error
// boundary cannot catch it and the entire app bundle fails to load. Instead we treat
// missing config as "map unavailable" and surface a fallback + logError at render time.
const geoConfig = awsExports?.geo?.amazon_location_service ?? null;
const region = geoConfig?.region;
const MAPS = geoConfig?.maps ?? null;
const GEO_CONFIGURED = Boolean(region && MAPS?.default && MAPS?.items);

// Log the missing-config condition at most once. We do this lazily (when a user
// actually opens the panel) rather than at import time, so window.appUser and the
// API client are initialized and we don't log for users who never view geo-fences.
let geoConfigErrorLogged = false;

const styleUrl = (mapName) =>
  `https://maps.geo.${region}.amazonaws.com/maps/v0/maps/${mapName}/style-descriptor`;

// Build [{ name, label }, ...] from aws-exports maps, with the default first.
const styleLabel = (mapStyle = "") => {
  const s = mapStyle.toLowerCase();
  if (s.includes("satellite") || s.includes("imagery")) return "Satellite";
  if (s.includes("street")) return "Streets";
  if (s.includes("topographic")) return "Topo";
  if (s.includes("navigation")) return "Nav";
  if (s.includes("light")) return "Light";
  if (s.includes("dark")) return "Dark";
  return mapStyle || "Map";
};
const MAP_OPTIONS = GEO_CONFIGURED
  ? Object.entries(MAPS.items)
      .map(([name, info]) => ({ name, label: styleLabel(info.style) }))
      .sort((a, b) => {
        if (a.name === MAPS.default) return -1;
        if (b.name === MAPS.default) return 1;
        return 0;
      })
  : [];

// MapLibre IControl that renders a row of style-toggle buttons.
class StyleSwitcherControl {
  constructor({ options, activeName, onSelect }) {
    this._options = options;
    this._active = activeName;
    this._onSelect = onSelect;
    this._buttons = new Map();
  }
  onAdd() {
    // Avoid `maplibregl-ctrl-group` — its CSS forces children to 29x29 icon-button size.
    this._container = document.createElement("div");
    this._container.className = "maplibregl-ctrl";
    this._container.style.cssText =
      "display:flex;background:#fff;border-radius:4px;overflow:hidden;box-shadow:0 0 0 2px rgba(0,0,0,0.1);";
    this._options.forEach(({ name, label }) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.textContent = label;
      btn.style.cssText =
        "padding:6px 12px;font-size:12px;line-height:1;border:none;border-right:1px solid #e0e0e0;cursor:pointer;font-family:inherit;white-space:nowrap;width:auto;height:auto;min-width:0;";
      btn.addEventListener("click", () => {
        if (name === this._active) return;
        this.setActive(name);
        this._onSelect(name);
      });
      this._buttons.set(name, btn);
      this._container.appendChild(btn);
    });
    // Drop the right-border on the last button.
    const last = this._container.lastElementChild;
    if (last) last.style.borderRight = "none";
    this._paint();
    return this._container;
  }
  setActive(name) {
    this._active = name;
    this._paint();
  }
  _paint() {
    this._buttons.forEach((btn, name) => {
      const active = name === this._active;
      btn.style.background = active ? "#1976d2" : "#fff";
      btn.style.color = active ? "#fff" : "#000";
      btn.style.fontWeight = active ? "600" : "400";
    });
  }
  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
  }
}

// MapLibre IControl with a single button that opens the full-screen dialog.
// Not maplibre's own FullscreenControl: that one requests the browser's
// fullscreen API on the map element, which escapes React's tree and leaves the
// form (and its Save button) unreachable. This just calls back into React.
class ExpandControl {
  constructor({ onClick, title = "Open large map" }) {
    this._onClick = onClick;
    this._title = title;
  }
  onAdd() {
    this._container = document.createElement("div");
    this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = this._title;
    btn.setAttribute("aria-label", this._title);
    // Inline SVG (four corner brackets) — no icon-font or React render inside a
    // MapLibre control container.
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">' +
      '<path d="M4 4h6v2H6v4H4V4zm10 0h6v6h-2V6h-4V4zM4 14h2v4h4v2H4v-6zm14 0h2v6h-6v-2h4v-4z"/>' +
      "</svg>";
    btn.style.cssText =
      "display:flex;align-items:center;justify-content:center;cursor:pointer;";
    btn.addEventListener("click", () => this._onClick());
    this._container.appendChild(btn);
    return this._container;
  }
  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
  }
}

// Sides offered by the shape slider. Index into this array is the slider value,
// which keeps the marks evenly spaced instead of scaled by the side count.
export const POLYGON_SIDE_OPTIONS = [4, 6, 8, 12, 16];
export const DEFAULT_POLYGON_SIDES = 8;

// Build a regular polygon inscribed in a circle of the given radius.
// Offset by π/sides so flat sides align with N/S/E/W (stop-sign orientation).
export const buildGeofencePolygon = (
  lat,
  lng,
  radiusMeters,
  sides = DEFAULT_POLYGON_SIDES,
) => {
  const offset = Math.PI / sides;
  const coords = [];
  for (let i = 0; i <= sides; i++) {
    const angle = offset + (i / sides) * 2 * Math.PI;
    const dLat = (radiusMeters / 111320) * Math.cos(angle);
    const dLng =
      (radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180))) *
      Math.sin(angle);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: "Polygon", coordinates: [coords] };
};

const metersToFeet = (m) => Math.round(m * 3.28084).toLocaleString();
const metersToKm = (m) => (m / 1000).toFixed(2);
const metersToMiles = (m) => (m / 1609.344).toFixed(2);
// Combined metric + imperial label for the radius slider.
const formatRadius = (m) =>
  `${m.toLocaleString()} m · ${metersToFeet(m)} ft  |  ${metersToKm(m)} km · ${metersToMiles(m)} mi`;

// Accept either a JSON string (TextInput value) or a parsed object (AppSync AWSJSON)
const normalizeGeofence = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (typeof value === "object") return value;
  return null;
};

// Vertex count of a stored boundary's outer ring, ignoring the closing point
// that repeats the first vertex. Returns null for anything but a simple Polygon
// (hand-drawn MultiPolygons have no single side count to show).
export const polygonSideCount = (geofence) => {
  const geojson = normalizeGeofence(geofence);
  if (geojson?.type !== "Polygon") return null;
  const ring = geojson.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 4) return null;
  const last = ring.length - 1;
  const closed = ring[0][0] === ring[last][0] && ring[0][1] === ring[last][1];
  return closed ? last : ring.length;
};

// ---- Device location diagnostic ----------------------------------------

/** Great-circle distance in meters between two [lng, lat] points. */
export const distanceMeters = ([lng1, lat1], [lng2, lat2]) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371008.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Where a device fix sits relative to the yard: meters from the yard
 * coordinates (null without coordinates) and inside/outside the boundary (null
 * without a boundary). Runs the same point-in-polygon test the kiosk runs at
 * the geo gate, so what the manager reads here is what a driver standing in
 * the same spot would get.
 */
export const assessDeviceFix = (fix, { lat, lng, geofence }) => {
  const point = [fix.lng, fix.lat];
  const distance = lat && lng ? distanceMeters(point, [lng, lat]) : null;
  const geojson = normalizeGeofence(geofence);
  const inside = geojson ? pointInGeofence(point, geojson) : null;
  return { distance, inside };
};

// GeolocationPositionError codes; the browser's own messages are terse and
// vary by vendor.
const GEOLOCATION_ERRORS = {
  1: "Location permission was denied for this browser.",
  2: "The device could not determine its position.",
  3: "Timed out waiting for a position.",
};

/**
 * One-shot device fix on demand. Asks for a fresh, high-accuracy reading every
 * time: a cached position would say where the device was, not how well it can
 * place itself right now, which is the whole point of the diagnostic.
 */
const useDeviceLocation = () => {
  const [state, setState] = useState({
    status: "idle",
    fix: null,
    error: null,
  });
  const locate = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({
        status: "error",
        fix: null,
        error: "This browser does not offer location.",
      });
      return;
    }
    setState((prev) => ({ ...prev, status: "locating", error: null }));
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setState({
          status: "done",
          error: null,
          fix: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            at: pos.timestamp,
          },
        }),
      (err) =>
        setState({
          status: "error",
          fix: null,
          error:
            GEOLOCATION_ERRORS[err?.code] ||
            err?.message ||
            "Could not read the device location.",
        }),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, []);
  return { ...state, locate };
};

const formatMeters = (m) =>
  `${Math.round(m).toLocaleString()} m (≈ ${metersToFeet(m)} ft)`;

/**
 * Button plus readout for the device pin on the map above it. Diagnostic only:
 * nothing here is saved. The readout states the fix, its reported accuracy,
 * the distance to the yard coordinates and whether the boundary contains it,
 * so a manager standing at the gate can tell a bad fence from a bad fix.
 */
const DeviceLocationCheck = ({ location, yard }) => {
  const { status, fix, error, locate } = location;
  const assessment = fix ? assessDeviceFix(fix, yard) : null;
  const label =
    status === "locating"
      ? "Locating…"
      : fix
        ? "Refresh my location"
        : "Show my location";
  return (
    <Box mt={1}>
      <Button
        label={label}
        size="medium"
        variant="outlined"
        onClick={locate}
        disabled={status === "locating"}
      />
      <Typography variant="caption" color="textSecondary" display="block">
        Diagnostic: drops a red pin where this device believes it is, with the
        reported accuracy as a shaded ring. Check from a phone at the gate; a
        desktop browser without GPS may place itself kilometres away.
      </Typography>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      {fix && assessment && (
        <Typography variant="body2" data-testid="device-fix-summary">
          Device at {fix.lat.toFixed(6)}, {fix.lng.toFixed(6)}, accurate to
          about {formatMeters(fix.accuracy)}.
          {assessment.distance != null &&
            ` ${formatMeters(assessment.distance)} from the yard coordinates.`}
          {assessment.inside === true && " Inside the boundary."}
          {assessment.inside === false && " Outside the boundary."} Fixed at{" "}
          {new Date(fix.at).toLocaleTimeString()}.
        </Typography>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  mapContainer: {
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },
  // The dialog map fills whatever the full-screen sheet leaves after the title
  // bar, so the container has to stretch rather than carry a pixel height.
  dialogContent: {
    display: "flex",
    padding: theme.spacing(0, 2, 2, 2),
    // The map (or its unavailable-fallback) is the only child; stretch it to the
    // full sheet width, since height:100% alone leaves a flex item zero-wide.
    "& > *": { flex: 1 },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const GeofenceMap = ({
  geofence,
  lat,
  lng,
  radiusMeters,
  sides,
  editable,
  onChange,
  onExpand,
  deviceFix,
  height = 320,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const switcherRef = useRef(null);
  const markersRef = useRef([]);
  // Latest device fix, read by the style.load handler so the accuracy ring
  // comes back after a style switch without re-binding the handler.
  const deviceFixRef = useRef(deviceFix);
  deviceFixRef.current = deviceFix;
  // Set true just before a vertex-drag writes back to the form, so the
  // displayGeoJSON effect re-installs the boundary but skips re-fitting the
  // viewport (which would yank the map out from under the user mid-edit).
  const skipFitRef = useRef(false);
  // Fit the viewport to the boundary only the first time one appears. Live
  // resizing (slider) and corner drags then leave the camera where the user
  // put it instead of jumping on every change.
  const didFitRef = useRef(false);
  // onChange can change identity each render; read the latest via ref so the
  // marker drag handlers don't need to be re-bound.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Same reason as onChange: the control is added once, at map creation.
  const onExpandRef = useRef(onExpand);
  onExpandRef.current = onExpand;
  // Map is created asynchronously (after Auth.currentCredentials), so we can't
  // rely on mapRef alone to know when it's safe to attach markers.
  const [mapReady, setMapReady] = useState(false);
  // maplibre-gl is loaded synchronously via a <script> tag in index.html, so it's
  // either present or it failed to load (CDN block, CSP, offline). Capture at
  // mount so render output stays consistent.
  const [mapLibreReady] = useState(
    () => typeof window !== "undefined" && !!window.maplibregl,
  );

  // Missing geo config is an environment/config problem, not a user action — log it
  // once so it surfaces in monitoring, but render a fallback rather than crashing.
  useEffect(() => {
    if (!GEO_CONFIGURED && !geoConfigErrorLogged) {
      geoConfigErrorLogged = true;
      logError(
        "GeoFencePanel: Amplify geo config (geo.amazon_location_service) missing from aws-exports",
        new Error(
          "awsExports.geo.amazon_location_service is undefined or incomplete",
        ),
      );
    }
  }, []);

  const geojson = normalizeGeofence(geofence);
  const isPreview = !geojson;
  const displayGeoJSON =
    geojson ??
    (lat && lng
      ? buildGeofencePolygon(
          lat,
          lng,
          radiusMeters ?? 500,
          sides ?? DEFAULT_POLYGON_SIDES,
        )
      : null);

  // Latest data kept in a ref so the style.load handler can read fresh values
  // without re-binding every render.
  const dataRef = useRef({ displayGeoJSON, isPreview });
  dataRef.current = { displayGeoJSON, isPreview };

  // Idempotently (re)add the boundary source + layers and paint with the current data.
  // Called on every style.load — both initial and after map.setStyle().
  const installBoundary = (map) => {
    const { displayGeoJSON: data, isPreview: preview } = dataRef.current;
    const featureData = data
      ? { type: "Feature", geometry: data }
      : { type: "FeatureCollection", features: [] };

    if (!map.getSource("boundary")) {
      map.addSource("boundary", { type: "geojson", data: featureData });
    } else {
      map.getSource("boundary").setData(featureData);
    }

    const fillColor = preview ? "#9e9e9e" : "#1976d2";
    const fillOpacity = preview ? 0.1 : 0.3;
    const lineColor = preview ? "#9e9e9e" : "#1976d2";
    const lineDash = preview ? [3, 3] : [1, 0];

    if (!map.getLayer("boundary-fill")) {
      map.addLayer({
        id: "boundary-fill",
        type: "fill",
        source: "boundary",
        paint: { "fill-color": fillColor, "fill-opacity": fillOpacity },
      });
    } else {
      map.setPaintProperty("boundary-fill", "fill-color", fillColor);
      map.setPaintProperty("boundary-fill", "fill-opacity", fillOpacity);
    }
    if (!map.getLayer("boundary-line")) {
      map.addLayer({
        id: "boundary-line",
        type: "line",
        source: "boundary",
        paint: {
          "line-color": lineColor,
          "line-width": 2,
          "line-dasharray": lineDash,
        },
      });
    } else {
      map.setPaintProperty("boundary-line", "line-color", lineColor);
      map.setPaintProperty("boundary-line", "line-dasharray", lineDash);
    }
  };

  // The device pin's accuracy ring is style data, so like the boundary it is
  // re-installed after every style switch. The pin itself is a DOM marker and
  // survives setStyle on its own.
  const installDeviceFix = (map) => {
    const fix = deviceFixRef.current;
    const data = fix
      ? {
          type: "Feature",
          geometry: buildGeofencePolygon(
            fix.lat,
            fix.lng,
            Math.max(fix.accuracy || 0, 1),
            36,
          ),
        }
      : { type: "FeatureCollection", features: [] };
    if (!map.getSource("device-accuracy")) {
      map.addSource("device-accuracy", { type: "geojson", data });
    } else {
      map.getSource("device-accuracy").setData(data);
    }
    if (!map.getLayer("device-accuracy-fill")) {
      map.addLayer({
        id: "device-accuracy-fill",
        type: "fill",
        source: "device-accuracy",
        paint: { "fill-color": "#d32f2f", "fill-opacity": 0.15 },
      });
      map.addLayer({
        id: "device-accuracy-line",
        type: "line",
        source: "device-accuracy",
        paint: {
          "line-color": "#d32f2f",
          "line-width": 1.5,
          "line-dasharray": [2, 2],
        },
      });
    }
  };

  // Initialize the map once on mount
  useEffect(() => {
    if (!mapLibreReady || !GEO_CONFIGURED) return;
    const mlgl = window.maplibregl;
    if (!mlgl || !containerRef.current) return;

    let cancelled = false;

    Auth.currentCredentials().then((creds) => {
      if (cancelled || !containerRef.current) return;

      const transformRequest = (url) => {
        if (!url.includes("amazonaws.com")) return { url };
        return {
          url: Signer.signUrl(
            url,
            {
              access_key: creds.accessKeyId,
              secret_key: creds.secretAccessKey,
              session_token: creds.sessionToken,
            },
            { service: "geo", region },
          ),
        };
      };

      const map = new mlgl.Map({
        container: containerRef.current,
        style: styleUrl(MAPS.default),
        transformRequest,
        center: [lng ?? 0, lat ?? 0],
        zoom: lat && lng ? 13 : 2,
        attributionControl: false,
      });
      map.addControl(
        new mlgl.AttributionControl({ compact: true }),
        "bottom-right",
      );

      if (MAP_OPTIONS.length > 1) {
        switcherRef.current = new StyleSwitcherControl({
          options: MAP_OPTIONS,
          activeName: MAPS.default,
          // diff:false forces a full style reload so the boundary source/layer
          // tear down cleanly and `style.load` fires reliably for re-install.
          onSelect: (name) => map.setStyle(styleUrl(name), { diff: false }),
        });
        map.addControl(switcherRef.current, "top-right");
      }

      if (onExpandRef.current) {
        map.addControl(
          new ExpandControl({ onClick: () => onExpandRef.current?.() }),
          "top-left",
        );
      }

      mapRef.current = map;

      // Fires on initial load and after every setStyle — keeps boundary in sync.
      map.on("style.load", () => {
        installBoundary(map);
        installDeviceFix(map);
        setMapReady(true);
      });
    });

    return () => {
      cancelled = true;
      switcherRef.current = null;
      setMapReady(false);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pan to new center when coordinates change after init
  useEffect(() => {
    if (mapRef.current && lat && lng) {
      mapRef.current.easeTo({ center: [lng, lat], duration: 400 });
    }
  }, [lat, lng]);

  // Update the boundary layer whenever the displayed GeoJSON changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => {
      if (!map.getSource("boundary")) return; // style still loading
      installBoundary(map);

      // A vertex drag already moved the boundary in place — re-fitting here
      // would recenter/zoom away from where the user is working.
      if (skipFitRef.current) {
        skipFitRef.current = false;
        return;
      }

      if (displayGeoJSON && !didFitRef.current) {
        const coords =
          displayGeoJSON.type === "MultiPolygon"
            ? displayGeoJSON.coordinates.flatMap((p) => p[0])
            : displayGeoJSON.coordinates[0];
        if (coords.length > 0) {
          const mlgl = window.maplibregl;
          if (mlgl) {
            const bounds = coords.reduce(
              (b, c) => b.extend(c),
              new mlgl.LngLatBounds(coords[0], coords[0]),
            );
            map.fitBounds(bounds, { padding: 40, maxZoom: 16 });
            didFitRef.current = true;
          }
        }
      }
    };

    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once("style.load", apply);
      return () => map.off("style.load", apply);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayGeoJSON, isPreview]);

  // Stable signature so the marker effect only rebuilds when vertices actually
  // change — displayGeoJSON gets a fresh object identity on every render.
  const geoKey =
    editable && !isPreview && displayGeoJSON?.type === "Polygon"
      ? JSON.stringify(displayGeoJSON.coordinates)
      : null;

  // Draggable corner handles. Each vertex of the polygon ring gets a MapLibre
  // marker; dragging it moves the boundary live and, on release, writes the
  // updated GeoJSON back to the form via onChange.
  useEffect(() => {
    const map = mapRef.current;
    const mlgl = window.maplibregl;
    if (!editable || !mapReady || !map || !mlgl || !geoKey) return;

    // Working copy of the outer ring (mutated in place as handles move).
    const ring = JSON.parse(geoKey)[0].map((c) => [...c]);
    const lastIdx = ring.length - 1;
    // GeoJSON rings are closed (last point == first); keep that invariant when
    // the first vertex moves, and don't render a duplicate handle for it.
    const closed =
      lastIdx >= 1 &&
      ring[0][0] === ring[lastIdx][0] &&
      ring[0][1] === ring[lastIdx][1];
    const vertexCount = closed ? lastIdx : ring.length;

    const markers = [];
    for (let i = 0; i < vertexCount; i++) {
      const el = document.createElement("div");
      el.style.cssText =
        "width:14px;height:14px;border-radius:50%;background:#1976d2;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.35);cursor:grab;box-sizing:border-box;";
      const marker = new mlgl.Marker({
        element: el,
        draggable: true,
        anchor: "center",
      })
        .setLngLat(ring[i])
        .addTo(map);

      marker.on("dragstart", () => {
        el.style.cursor = "grabbing";
      });
      marker.on("drag", () => {
        const { lng, lat } = marker.getLngLat();
        ring[i] = [lng, lat];
        if (i === 0 && closed) ring[lastIdx] = [lng, lat];
        const src = map.getSource("boundary");
        if (src) {
          src.setData({
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [ring] },
          });
        }
      });
      marker.on("dragend", () => {
        el.style.cursor = "grab";
        skipFitRef.current = true;
        if (onChangeRef.current) {
          onChangeRef.current({ type: "Polygon", coordinates: [ring] });
        }
      });
      markers.push(marker);
    }
    markersRef.current = markers;

    return () => {
      markers.forEach((m) => m.remove());
      markersRef.current = [];
    };
  }, [editable, mapReady, geoKey]);

  // Device pin: red marker at the fix, accuracy ring under it, and the camera
  // framed on fence plus pin so a fix far off the yard is seen for what it is
  // instead of being scrolled out of view.
  useEffect(() => {
    const map = mapRef.current;
    const mlgl = window.maplibregl;
    if (!map || !mlgl || !mapReady) return;
    installDeviceFix(map);
    if (!deviceFix) return;

    const marker = new mlgl.Marker({ color: "#d32f2f" })
      .setLngLat([deviceFix.lng, deviceFix.lat])
      .addTo(map);

    const bounds = new mlgl.LngLatBounds(
      [deviceFix.lng, deviceFix.lat],
      [deviceFix.lng, deviceFix.lat],
    );
    const { displayGeoJSON: data } = dataRef.current;
    const ring = !data
      ? []
      : data.type === "MultiPolygon"
        ? data.coordinates.flatMap((p) => p[0])
        : data.coordinates[0];
    ring.forEach((c) => bounds.extend(c));
    if (lat && lng) bounds.extend([lng, lat]);
    map.fitBounds(bounds, { padding: 60, maxZoom: 18 });

    return () => marker.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, deviceFix]);

  const fallback = (message) => (
    <Box
      height={height}
      border={1}
      borderColor="divider"
      borderRadius="borderRadius"
      bgcolor="action.hover"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      p={2}
    >
      <Typography variant="body2" color="textSecondary">
        {message}
      </Typography>
    </Box>
  );

  if (!GEO_CONFIGURED) {
    return fallback(
      "Map preview unavailable — the location service is not configured for this environment.",
    );
  }

  if (!mapLibreReady) {
    return fallback(
      "Map preview unavailable — the map library could not be loaded. Check your network connection or content-blocking extensions, then reload.",
    );
  }

  return (
    <div
      ref={containerRef}
      className={classes.mapContainer}
      style={{ height }}
    />
  );
};

/**
 * Inline map plus a full-screen dialog copy of it. The 320px inline strip is too
 * cramped to drag corners in, so the expand control on the map opens the same
 * map at sheet size.
 *
 * The dialog renders a second, independent GeofenceMap rather than moving the
 * inline one: MapLibre instances don't survive being re-parented into another
 * DOM subtree. Both read the same `geofence` prop and write through the same
 * onChange, so edits made in either view show up in the other — the dialog map
 * is created on open and destroyed on close.
 *
 * Only one of the two is ever mounted. The inline map is entirely covered while
 * the dialog is open, and a live MapLibre instance nobody can see still holds a
 * WebGL context and repaints on every vertex drag made in the dialog. It comes
 * back on close, reading the boundary the dialog just wrote.
 */
// Exported for tests: whether the inline map is torn down while the sheet is
// open is a mount question, and the only way to ask it is to drive this
// component directly.
export const GeofenceMapWithFullscreen = ({
  title = "Geo-fence boundary",
  ...props
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      {expanded ? (
        // Holds the strip's place so the page behind the sheet keeps its
        // height, and closing the dialog does not land the manager somewhere
        // else on the form.
        <div style={{ height: props.height ?? 320 }} />
      ) : (
        <GeofenceMap {...props} onExpand={() => setExpanded(true)} />
      )}
      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        fullScreen
        aria-labelledby="geofence-map-dialog-title"
      >
        <DialogTitle
          id="geofence-map-dialog-title"
          disableTypography
          className={classes.dialogTitle}
        >
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={() => setExpanded(false)} aria-label="Close map">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <GeofenceMap {...props} height="100%" />
        </DialogContent>
      </Dialog>
    </>
  );
};

const GeoFencePanel = ({ record, disabled }) => {
  if (disabled) return <GeoFenceShow record={record} />;
  return <GeoFenceEdit record={record} />;
};

// Exported for tests: the coordinate/side-count interplay below is form-state
// behavior that can't be reached through the default export without a Show/Edit
// wrapper.
export const GeoFenceEdit = ({ record }) => {
  const form = useForm();
  const notify = useNotify();
  const deviceLocation = useDeviceLocation();

  // These fields have no rendered input anymore (the slider and map drive them
  // via form.change). Register them so final-form tracks them for the form's
  // dirty/pristine state — otherwise changes here never enable the Save button.
  useField("geofence", { subscription: {} });
  useField("geofenceToleranceMeters", { subscription: {} });

  // Watched so the boundary can be built the moment coordinates arrive — see the
  // effect below. NumberInput/geocode both write these through final-form.
  const {
    input: { value: latitude },
  } = useField("latitude", { subscription: { value: true } });
  const {
    input: { value: longitude },
  } = useField("longitude", { subscription: { value: true } });

  const [radiusLocal, setRadiusLocal] = useState(
    record?.geofenceToleranceMeters ?? 500,
  );
  // Side count isn't persisted on its own — it lives in the saved boundary's
  // vertex count. Recover it on mount so reopening the panel shows the shape
  // that's actually stored; fall back to the default for hand-edited boundaries
  // whose corner count isn't one of the slider's options.
  const [sidesLocal, setSidesLocal] = useState(() => {
    const stored = polygonSideCount(record?.geofence);
    return POLYGON_SIDE_OPTIONS.includes(stored)
      ? stored
      : DEFAULT_POLYGON_SIDES;
  });

  const geocodeFromAddress = async () => {
    const { street, city, state, zipCode, country } = form.getState().values;
    const query = [street, city, state, zipCode, country]
      .filter(Boolean)
      .join(", ");
    try {
      const results = await Geo.searchByText(query, { maxResults: 1 });
      if (results?.[0]) {
        const [lng, lat] = results[0].geometry.point;
        form.change("latitude", lat);
        form.change("longitude", lng);
        notify("Coordinates updated from address.", "info");
      } else {
        notify("Address not found — enter coordinates manually.", "warning");
      }
    } catch (err) {
      logError("GeoFencePanel:geocodeFromAddress", err, notify);
    }
  };

  // Regenerate the boundary from the current coordinates + radius + side count.
  // No-op until coordinates exist (the on-save transform still backfills one if
  // needed).
  const regeneratePolygon = (radius, sides) => {
    const { latitude, longitude } = form.getState().values;
    if (!latitude || !longitude) return;
    const polygon = buildGeofencePolygon(latitude, longitude, radius, sides);
    form.change("geofence", JSON.stringify(polygon, null, 2));
  };

  // The sliders are usable before the yard has coordinates, and a slider move
  // that early can't write a boundary. Build one as soon as coordinates appear so
  // the shape the manager picked is what gets saved — otherwise the on-save
  // fallback in ReloadYardEdit regenerates with the default side count and the
  // stored fence silently disagrees with the preview.
  //
  // Fires only on the empty-to-set transition, never on mount: writing a boundary
  // just because the panel was opened would dirty the form with a change the
  // manager never made. An existing boundary (stored or corner-dragged) is left
  // alone; the on-save fallback still covers a yard whose coordinates were filled
  // in outside this panel.
  const hadCoordsRef = useRef(Boolean(latitude && longitude));
  useEffect(() => {
    const hasCoords = Boolean(latitude && longitude);
    const gainedCoords = hasCoords && !hadCoordsRef.current;
    hadCoordsRef.current = hasCoords;
    if (gainedCoords && !form.getState().values.geofence)
      regeneratePolygon(radiusLocal, sidesLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  // Persist a corner-drag from the map back into the boundary.
  const handleVertexDrag = (polygon) => {
    form.change("geofence", JSON.stringify(polygon, null, 2));
  };

  // Resize live while dragging the slider. We regenerate on every onChange
  // (not onChangeCommitted) so the boundary tracks the thumb — and so we never
  // fire a state update inside MUI's mouseup teardown, which would otherwise
  // leave the slider stuck and unresponsive to the next click.
  const handleRadiusSlider = (_, value) => {
    setRadiusLocal(value);
    form.change("geofenceToleranceMeters", value);
    regeneratePolygon(value, sidesLocal);
  };

  // Slider value is the index into POLYGON_SIDE_OPTIONS so the marks sit evenly
  // apart rather than spaced by side count (4, 6, 8, 12, 16 is not linear).
  const handleSidesSlider = (_, index) => {
    const sides = POLYGON_SIDE_OPTIONS[index];
    if (!sides || sides === sidesLocal) return;
    setSidesLocal(sides);
    regeneratePolygon(radiusLocal, sides);
  };

  return (
    <FormDataConsumer>
      {({ formData }) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Driver Geo-fence</Typography>
            <Typography variant="body2" color="textSecondary">
              Define a boundary around the yard. Driver check-in always records
              the device position; turn on enforcement below to also block
              check-in from outside the boundary.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <BooleanInput
              source="geofenceEnabled"
              label="Enable geo-fence"
              helperText={false}
            />
          </Grid>

          {formData.geofenceEnabled && (
            <>
              {/* Coordinates */}
              <Grid item xs={12}>
                <Typography variant="overline">Yard Coordinates</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput
                  source="latitude"
                  label="Latitude"
                  variant="outlined"
                  fullWidth
                  helperText={false}
                  inputProps={{ step: "0.000001" }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <NumberInput
                  source="longitude"
                  label="Longitude"
                  variant="outlined"
                  fullWidth
                  helperText={false}
                  inputProps={{ step: "0.000001" }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* Match the dense outlined inputs beside it: 8px top, 4px bottom margin, 56px tall */}
                <Button
                  label="Auto-fill from address"
                  size="large"
                  variant="outlined"
                  onClick={geocodeFromAddress}
                  style={{ marginTop: 7 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Radius + shape */}
              <Grid item xs={12}>
                <Typography variant="overline">Polygon Boundary</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Drag the sliders to size and shape the boundary — it
                  regenerates automatically around the yard coordinates.
                  Fine-tune individual corners by dragging them on the map.
                </Typography>
                <Box px={1}>
                  <Slider
                    value={radiusLocal}
                    min={50}
                    max={5000}
                    step={50}
                    onChange={handleRadiusSlider}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v.toLocaleString()} m`}
                    marks={[
                      { value: 100, label: "100 m" },
                      { value: 500, label: "500 m" },
                      { value: 1000, label: "1 km" },
                      { value: 2500, label: "2.5 km" },
                      { value: 5000, label: "5 km" },
                    ]}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Radius: {formatRadius(radiusLocal)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="overline">Sides</Typography>
                <Box px={1}>
                  <Slider
                    value={POLYGON_SIDE_OPTIONS.indexOf(sidesLocal)}
                    min={0}
                    max={POLYGON_SIDE_OPTIONS.length - 1}
                    step={null}
                    onChange={handleSidesSlider}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(i) => POLYGON_SIDE_OPTIONS[i]}
                    marks={POLYGON_SIDE_OPTIONS.map((sides, i) => ({
                      value: i,
                      label: String(sides),
                    }))}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  {sidesLocal} sides — more sides hug a circle more closely.
                  Changing this rebuilds the boundary and discards corner
                  adjustments.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Map preview */}
              <Grid item xs={12}>
                <Typography variant="overline">Boundary Preview</Typography>
                {formData.latitude && formData.longitude ? (
                  <>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      gutterBottom
                    >
                      {formData.geofence
                        ? "Drag the corner handles on the map to adjust the boundary — use the expand button for a full-screen map."
                        : "Dashed outline previews the polygon that will be saved. Adjust the sliders above to resize or reshape it."}
                    </Typography>
                    <GeofenceMapWithFullscreen
                      title="Edit geo-fence boundary"
                      geofence={formData.geofence}
                      lat={formData.latitude}
                      lng={formData.longitude}
                      radiusMeters={
                        formData.geofenceToleranceMeters ?? radiusLocal
                      }
                      sides={sidesLocal}
                      editable
                      onChange={handleVertexDrag}
                      deviceFix={deviceLocation.fix}
                    />
                    <DeviceLocationCheck
                      location={deviceLocation}
                      yard={{
                        lat: formData.latitude,
                        lng: formData.longitude,
                        geofence: formData.geofence,
                      }}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Enter coordinates above to see a map preview.
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const GeoFenceShow = ({ record }) => {
  const enabled = record?.geofenceEnabled;
  const sides = polygonSideCount(record?.geofence);
  const deviceLocation = useDeviceLocation();

  if (!enabled)
    return (
      <Typography variant="body2" color="textSecondary">
        Geo-fence is disabled.
      </Typography>
    );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          {record?.geofenceEnforced
            ? "Enforced — drivers must be inside the boundary to check in."
            : "Not enforced — driver positions are recorded but never block check-in."}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="Latitude">
          <TextField record={record} source="latitude" />
        </Labeled>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="Longitude">
          <TextField record={record} source="longitude" />
        </Labeled>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="Radius">
          <Typography>
            {record?.geofenceToleranceMeters
              ? `${record.geofenceToleranceMeters} m (≈ ${metersToFeet(record.geofenceToleranceMeters)} ft)`
              : "—"}
          </Typography>
        </Labeled>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="Sides">
          <Typography>{sides ?? "—"}</Typography>
        </Labeled>
      </Grid>
      {record?.latitude && record?.longitude && (
        <Grid item xs={12}>
          <GeofenceMapWithFullscreen
            geofence={record.geofence}
            lat={record.latitude}
            lng={record.longitude}
            radiusMeters={record.geofenceToleranceMeters}
            deviceFix={deviceLocation.fix}
          />
          <DeviceLocationCheck
            location={deviceLocation}
            yard={{
              lat: record.latitude,
              lng: record.longitude,
              geofence: record.geofence,
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default GeoFencePanel;
